import {PaymentProofs} from '../dashboard/PaymentProofs';
import React, {useEffect, useState} from 'react';
import {Reservation, Table, User, UserRole} from '../dashboard/types';
import { getStatusBadge } from '../ui/ReservationStatusBadge';

const formatCreatedAt = (value: string) => new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Africa/Dar_es_Salaam',
}).format(new Date(value));

interface ReservationDetailModalProps {
  apiBase: string;
  token: string;
  canViewProofs: boolean;
  selectedReservation: Reservation | null;
  reservationDrafts: Record<string, Partial<Reservation>>;
  updateReservationDraft: (
    id: string,
    key: keyof Reservation,
    value: string | string[],
  ) => void;
  tables: Table[];
  onClose: () => void;
  onSave: (id: string) => Promise<boolean>;
  canEdit: boolean;
  saveError?: string;
}

export const ReservationDetailModal: React.FC<ReservationDetailModalProps> = ({
  apiBase, token, canViewProofs,
  selectedReservation,
  reservationDrafts,
  updateReservationDraft,
  tables,
  onClose,
  onSave,
  canEdit, saveError,
}) => {
  if (!selectedReservation) {
    return null;
  }

  const additionalTableIds = (reservationDrafts[selectedReservation.id]?.additionalTableIds as string[] | undefined)
    ?? (selectedReservation.additionalTables || []).map((table) => table.id);
  const assignableTables = tables.filter((table) => table.id !== selectedReservation.table?.id && (!table.reserved || additionalTableIds.includes(table.id)));
  const assignedCapacity = Number(selectedReservation.table?.capacity || 0) + assignableTables.filter((table) => additionalTableIds.includes(table.id)).reduce((total, table) => total + table.capacity, 0);

  return (
    <div
      className='reservation-modal-overlay'
      onClick={onClose}>
      <div
        className='reservation-modal'
        role='dialog'
        aria-modal='true'
        aria-label='Reservation details'
        onClick={(event) => event.stopPropagation()}>
        <div className='reservation-modal-header'>
          <h3>{selectedReservation.eventId ? 'Event Reservation' : 'Reservation Details'}</h3>
          <button
            type='button'
            className='reservation-close'
            onClick={onClose}>
            Close
          </button>
        </div>

        {canViewProofs && <PaymentProofs key={selectedReservation.id} apiBase={apiBase} token={token} reservationId={selectedReservation.id} />}

        <div className='bg-blue-50 border border-blue-100 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
          <div className='ms-2'>
            <p>
              <strong>Customer:</strong> {selectedReservation.customerName}
            </p>
            <p>
              <strong>Email:</strong> {selectedReservation.customerEmail}
            </p>
            <p>
              <strong>Phone:</strong>{' '}
              {selectedReservation.customerPhone || 'Not provided'}
            </p>
            <p>
              <strong>Status:</strong>{' '}
              {getStatusBadge(selectedReservation.status)}
            </p>
            <p>
              <strong>Created:</strong>{' '}
              {formatCreatedAt(selectedReservation.createdAt)}
            </p>
          </div>
          {selectedReservation.table && (
            <div className='ms-2'>
              <p>Table Details</p>
              <p>
                <strong>Primary table: </strong>{' '}
                <span>{selectedReservation.table?.description || `Table ${selectedReservation.table?.tableNumber}`}</span>
              </p>
              <p>
                <strong>Section: </strong>{' '}
                <span>{selectedReservation.table?.section?.name || 'Not specified'}</span>
              </p>
              <p>
                <strong>VIP:</strong>{' '}
                <span
                  className={`px-2 text-sm rounded-lg ${selectedReservation.table?.isVip ? ' bg-yellow-500 text-black' : 'bg-gray-200 text-gray-500'}`}>
                  {selectedReservation.table?.isVip ? 'Yes' : 'No'}
                </span>
              </p>
              <p>
                <strong>Capacity:</strong>{' '}
                <span className={``}>
                  {selectedReservation.table?.capacity} seats
                </span>
              </p>
              {selectedReservation.additionalTables && selectedReservation.additionalTables.length > 0 && <div>
                <strong>Additional tables:</strong>
                <ul className='mt-1 list-disc pl-5'>
                  {selectedReservation.additionalTables.map((table) => <li key={table.id}>{table.description || `Table ${table.tableNumber}`} — {table.section?.name || 'Section not specified'}</li>)}
                </ul>
              </div>}
            </div>
          )}
        </div>

        {selectedReservation.eventId && <p className='mb-4 rounded bg-orange-50 p-3'><strong>{selectedReservation.event?.title || 'Event reservation'}</strong><br/>{selectedReservation.eventStartsAt && formatCreatedAt(selectedReservation.eventStartsAt)} – {selectedReservation.eventEndsAt && formatCreatedAt(selectedReservation.eventEndsAt)} EAT<br/>The event schedule, guest count and seating are fixed for this booking.</p>}
        <div className='reservation-form-grid'>
          <label htmlFor='detail-date'>Date</label>
          <input
            id='detail-date'
            type='date'
            disabled={!canEdit || Boolean(selectedReservation.eventId)}
            value={
              (reservationDrafts[selectedReservation.id]?.date as string) ||
              selectedReservation.date
            }
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'date',
                e.target.value,
              )
            }
          />

          <label htmlFor='detail-time'>Time</label>
          <input
            id='detail-time'
            type='time'
            disabled={!canEdit || Boolean(selectedReservation.eventId)}
            value={
              (reservationDrafts[selectedReservation.id]
                ?.timeStart as string) || selectedReservation.timeStart
            }
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'timeStart',
                e.target.value,
              )
            }
          />

          <label htmlFor='detail-time-end'>Time End</label>
          <input
            id='detail-time-end'
            type='time'
            disabled={!canEdit || Boolean(selectedReservation.eventId)}
            value={
              (reservationDrafts[selectedReservation.id]
                ?.timeEnd as string) || selectedReservation.timeEnd || ''
            }
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'timeEnd',
                e.target.value,
              )
            }
          />

          <label htmlFor='detail-guests'>Guests</label>
          <input
            id='detail-guests'
            type='number'
            disabled={!canEdit || Boolean(selectedReservation.eventId)}
            min={1}
            value={String(
              (reservationDrafts[selectedReservation.id]
                ?.numberOfGuests as number) ||
                selectedReservation.numberOfGuests,
            )}
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'numberOfGuests',
                e.target.value,
              )
            }
          />

          <label htmlFor='detail-status'>Status</label>
          <select
            id='detail-status'
            disabled={!canEdit}
            value={
              (reservationDrafts[selectedReservation.id]?.status as string) ||
              selectedReservation.status
            }
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'status',
                e.target.value,
              )
            }>
            {selectedReservation.status === 'pending_payment' && <option value='pending_payment'>Pending payment</option>}
            <option value='booked'>Booked</option>
            <option value='confirmed'>Confirmed</option>
            <option value='checked_in'>Checked In</option>
            <option value='cancelled'>Cancelled</option>
            <option value='paid'>Paid</option>
            <option value='completed'>Completed</option>
          </select>

          {/* Show paid amount field if status is paid_partially or paid_fully */}
          {['paid_partially', 'paid'].includes(
            (reservationDrafts[selectedReservation.id]?.status as string) || selectedReservation.status
          ) && (
            <>
              <label htmlFor='detail-paid-amount'>Paid Amount</label>
              <input
                id='detail-paid-amount'
                type='number'
                disabled={!canEdit || Boolean(selectedReservation.eventId)}
                min={0}
                value={
                  (reservationDrafts[selectedReservation.id]?.paidAmount as number) ??
                  selectedReservation.paidAmount ?? ''
                }
                onChange={(e) =>
                  updateReservationDraft(
                    selectedReservation.id,
                    'paidAmount',
                    e.target.value,
                  )
                }
              />
            </>
          )}

          {!selectedReservation.eventId && selectedReservation.numberOfGuests > 7 && <fieldset className='reservation-notes border border-gray-200 rounded-lg p-3'>
            <legend className='font-semibold px-1'>Assigned tables</legend>
            <p className='mb-2 text-sm'>Capacity: {assignedCapacity} seats for {selectedReservation.numberOfGuests} guests</p>
            <div className='grid gap-2'>
              {assignableTables.map((table) => <label key={table.id} className='flex items-center gap-2'>
                <input
                  type='checkbox'
                  disabled={!canEdit}
                  checked={additionalTableIds.includes(table.id)}
                  onChange={(event) => updateReservationDraft(
                    selectedReservation.id,
                    'additionalTableIds',
                    event.target.checked ? [...additionalTableIds, table.id] : additionalTableIds.filter((id) => id !== table.id),
                  )}
                />
                <span>{table.description || `Table ${table.tableNumber}`} — {table.section?.name || 'No section'} ({table.capacity} seats)</span>
              </label>)}
            </div>
          </fieldset>}

          <label htmlFor='detail-notes'>Notes</label>
          <textarea
            id='detail-notes'
            className='reservation-notes'
            disabled={!canEdit}
            value={
              (reservationDrafts[selectedReservation.id]?.notes as string) ||
              selectedReservation.notes ||
              ''
            }
            onChange={(e) =>
              updateReservationDraft(
                selectedReservation.id,
                'notes',
                e.target.value,
              )
            }
          />
        </div>

        {saveError && <p role='alert' className='text-red-600'>{saveError}</p>}
        <div className='w-full flex justify-between gap-2 mt-6 '>
          {canEdit && <button
            type='button'
            className='bg-blue-400 text-white cursor-pointer rounded-lg'
            onClick={async () => {
              const saved = await onSave(selectedReservation.id);
              if (saved) {
                onClose();
              }
            }}>
            Save Changes
          </button>}
          <button
            type='button'
            className='bg-gray-200 text-gray-700 px-4 py-2 rounded-lg cursor-pointer'
            onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

interface UserDetailModalProps {
  selectedUser: User | null;
  onClose: () => void;
  onSave: (id: string, updates: Partial<User> & {password?: string}) => Promise<boolean>;
}

export const UserDetailModal: React.FC<UserDetailModalProps> = ({
  selectedUser,
  onClose,
  onSave,
}) => {
  const [draft, setDraft] = useState({name: '', email: '', role: 'user' as UserRole, isActive: true, isDeleted: false, password: ''});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (selectedUser) setDraft({name: selectedUser.name, email: selectedUser.email, role: selectedUser.role, isActive: selectedUser.isActive, isDeleted: selectedUser.isDeleted, password: ''});
  }, [selectedUser]);

  if (!selectedUser) {
    return null;
  }

  return (
    <div
      className='user-modal-overlay'
      onClick={onClose}>
      <div
        className='user-modal'
        role='dialog'
        aria-modal='true'
        aria-label='User details'
        onClick={(event) => event.stopPropagation()}>
        <div className='reservation-modal-header'>
          <h3>User Details</h3>
          <button
            type='button'
            className='reservation-close'
            onClick={onClose}>
            Close
          </button>
        </div>

        <form className='user-edit-form' onSubmit={async (event) => {
          event.preventDefault();
          setIsSaving(true);
          const updates = {...draft, password: draft.password || undefined};
          const saved = await onSave(selectedUser.id, updates);
          setIsSaving(false);
          if (saved) onClose();
        }}>
        <div className='user-details-grid'>
          <label className='user-edit-field'><span>Name</span><input value={draft.name} onChange={(event) => setDraft({...draft, name: event.target.value})} required /></label>
          <label className='user-edit-field'><span>Email</span><input type='email' value={draft.email} onChange={(event) => setDraft({...draft, email: event.target.value})} required /></label>
          <label className='user-edit-field'><span>Role</span><select value={draft.role} onChange={(event) => setDraft({...draft, role: event.target.value as UserRole})}><option value='user'>User</option><option value='manager'>Manager</option><option value='admin'>Admin</option></select></label>
          <label className='user-edit-field'><span>New password</span><input type='password' minLength={8} value={draft.password} onChange={(event) => setDraft({...draft, password: event.target.value})} placeholder='Leave blank to keep current' /></label>
          <label className='user-edit-check'><input type='checkbox' checked={draft.isActive} onChange={(event) => setDraft({...draft, isActive: event.target.checked})} /><span>Active account</span></label>
          <label className='user-edit-check'><input type='checkbox' checked={draft.isDeleted} onChange={(event) => setDraft({...draft, isDeleted: event.target.checked})} /><span>Mark as deleted</span></label>
          <div className='user-detail-item'>
            <p className='profile-label'>Last Login</p>
            <p>
              {selectedUser.lastLogin
                ? new Date(selectedUser.lastLogin).toLocaleString()
                : 'Not available'}
            </p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Last Login IP</p>
            <p>{selectedUser.lastLoginIp || 'Not available'}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Created At</p>
            <p>{new Date(selectedUser.createdAt).toLocaleString()}</p>
          </div>
          <div className='user-detail-item'>
            <p className='profile-label'>Updated At</p>
            <p>{new Date(selectedUser.updatedAt).toLocaleString()}</p>
          </div>
        </div>
        <div className='user-edit-actions'><button type='button' onClick={onClose}>Cancel</button><button type='submit' className='create-user-primary' disabled={isSaving}>{isSaving ? 'Saving…' : 'Save Changes'}</button></div>
        </form>
      </div>
    </div>
  );
};
